import type { ReactNode } from 'react';
import { Spinner, StatusBadge, type StatusBadgeTone } from '@immo/ui-kit';
import type { FleetBoardTag } from '@immo/api-contract';
import {
  AlertCircleIcon,
  CheckIcon,
  ClockIcon,
  HalfCircleIcon,
  LockIcon,
  MinusCircleIcon,
  UserIcon,
  WarningTriangleIcon,
} from '@/components/layout/icons';
import { useTranslations } from '@/i18n/use-translations';

const ICON = 'h-3.5 w-3.5';

const TAGS: Record<FleetBoardTag, { tone: StatusBadgeTone; icon: ReactNode }> = {
  running: { tone: 'info', icon: <Spinner size="sm" /> },
  silent: { tone: 'danger', icon: <AlertCircleIcon className={ICON} /> },
  stranded: { tone: 'warning', icon: <WarningTriangleIcon className={ICON} /> },
  free: { tone: 'success', icon: <span aria-hidden className="h-2 w-2 rounded-full bg-current" /> },
  answered: { tone: 'success', icon: <CheckIcon className={ICON} /> },
  bound: { tone: 'neutral', icon: <LockIcon className={ICON} /> },
  parked: { tone: 'danger', icon: <HalfCircleIcon className={ICON} /> },
  assisted: { tone: 'neutral', icon: <UserIcon className={ICON} /> },
  capped: { tone: 'warning', icon: <MinusCircleIcon className={ICON} /> },
  waiting: { tone: 'neutral', icon: <ClockIcon className={ICON} /> },
};

/** The tags of a ticket the lane does not take: the card sits on a muted background. */
export const UNTAKEN_TAGS: ReadonlyArray<FleetBoardTag> = ['bound', 'parked', 'assisted', 'capped', 'waiting'];

export function CardTag({ tag }: { readonly tag: FleetBoardTag }) {
  const t = useTranslations('pages.admin.fleet-board');
  const { tone, icon } = TAGS[tag];

  return <StatusBadge size="sm" tone={tone} label={t(`tags.${tag}`)} icon={icon} />;
}
